/**
 * Global Light/Dark/System theming. Default = dark.
 * Persists via localStorage; applies via .dark on document.documentElement.
 * Call initTheme() at the very top of main.jsx before React renders to avoid flash.
 */

export const THEME_KEY = "tm_theme";

/** All allowed stored theme values. */
export const THEMES = ["light", "dark", "system"];

const VALID_STORED = THEMES;
const DEFAULT_STORED = "dark";

let _mq = null;
let _mqHandler = null;

function _attachSystemListener() {
  _removeSystemListener();
  if (typeof window === "undefined" || !window.matchMedia) return;
  _mq = window.matchMedia("(prefers-color-scheme: dark)");
  _mqHandler = () => {
    if (getStoredTheme() === "system") applyTheme(resolveTheme("system"));
  };
  _mq.addEventListener("change", _mqHandler);
}

function _removeSystemListener() {
  if (_mq && _mqHandler) {
    _mq.removeEventListener("change", _mqHandler);
    _mq = null;
    _mqHandler = null;
  }
}

/**
 * @returns {'light'|'dark'|'system'}
 */
export function getStoredTheme() {
  try {
    const v = String(localStorage.getItem(THEME_KEY) || "").trim().toLowerCase();
    return VALID_STORED.includes(v) ? v : DEFAULT_STORED;
  } catch (_) {
    return DEFAULT_STORED;
  }
}

/**
 * Returns the stored theme string. Alias for getStoredTheme for backward compatibility.
 * @returns {'light'|'dark'|'system'}
 */
export function getTheme() {
  return getStoredTheme();
}

/**
 * Resolves stored preference to effective theme. Uses prefers-color-scheme when stored === 'system'.
 * @param {'light'|'dark'|'system'} storedTheme
 * @returns {'light'|'dark'}
 */
export function resolveTheme(storedTheme) {
  if (storedTheme === "light") return "light";
  if (storedTheme === "dark") return "dark";
  if (storedTheme === "system" && typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return DEFAULT_STORED;
}

/**
 * Toggles .dark on document.documentElement. theme must be 'light' or 'dark'.
 * @param {'light'|'dark'} theme
 */
export function applyTheme(theme) {
  const root = document.documentElement;
  if (!root) return;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

/**
 * Saves stored theme and applies it. Dispatches 'theme-change' for UI sync.
 * setTheme('system') starts the matchMedia listener; setTheme('light'|'dark') stops it.
 * @param {'light'|'dark'|'system'} storedTheme
 */
export function setTheme(storedTheme) {
  const t = VALID_STORED.includes(storedTheme) ? storedTheme : DEFAULT_STORED;
  _removeSystemListener();
  try {
    localStorage.setItem(THEME_KEY, t);
  } catch (_) {}
  applyTheme(resolveTheme(t));
  if (t === "system") _attachSystemListener();
  try {
    window.dispatchEvent(new CustomEvent("theme-change", { detail: t }));
  } catch (_) {}
}

/**
 * Call once at the very top of main.jsx (before React renders).
 * If no stored value, sets 'dark' and applies. When stored === 'system', attaches matchMedia listener; otherwise removes any listener.
 */
export function initTheme() {
  let stored = null;
  try {
    const v = String(localStorage.getItem(THEME_KEY) || "").trim().toLowerCase();
    stored = VALID_STORED.includes(v) ? v : null;
  } catch (_) {}

  if (stored == null || stored === "") {
    try {
      localStorage.setItem(THEME_KEY, DEFAULT_STORED);
    } catch (_) {}
    stored = DEFAULT_STORED;
  }

  applyTheme(resolveTheme(stored));

  if (stored === "system") {
    _attachSystemListener();
  } else {
    _removeSystemListener();
  }
}
